import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const ProductSchemaForm = ({ onGenerate }) => {
  const productValidationSchema = Yup.object({
    name: Yup.string().required("Product name is required"),
    description: Yup.string().required("Product description is required"),
    price: Yup.number()
      .required("Price is required")
      .positive("Price must be positive"),
    image: Yup.string().url("Invalid image URL").required("Image URL is required"),
    brandName: Yup.string().required("Brand name is required"),
    brandImageLink: Yup.string()
      .url("Invalid brand image URL")
      .required("Brand image link is required"),
    brandLink: Yup.string()
      .url("Invalid brand link")
      .required("Brand link is required"),
    ratingValue: Yup.number()
      .required("Rating value is required")
      .min(0, "Rating value must be 0 or greater")
      .max(5, "Rating value cannot exceed 5"),
    reviewCount: Yup.number()
      .required("Review count is required")
      .integer("Review count must be an integer")
      .positive("Review count must be positive"),
    reviews: Yup.array().of(
      Yup.object({
        reviewBody: Yup.string().required("Review body is required"),
        reviewerName: Yup.string().required("Reviewer name is required"),
        ratingValue: Yup.number()
          .required("Rating value is required")
          .min(0, "Rating value must be 0 or greater")
          .max(5, "Rating value cannot exceed 5"),
      })
    ),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      price: "",
      image: "",
      brandName: "",
      brandImageLink: "",
      brandLink: "",
      ratingValue: "",
      reviewCount: "",
      reviews: [{ reviewBody: "", reviewerName: "", ratingValue: "" }],
    },
    validationSchema: productValidationSchema,
    onSubmit: (values) => {
      const schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: values.name,
        description: values.description,
        image: values.image,
        brand: {
          "@type": "Brand",
          name: values.brandName,
          image: values.brandImageLink,
          url: values.brandLink,
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: values.ratingValue,
          reviewCount: values.reviewCount,
        },
        offers: {
          "@type": "Offer",
          priceCurrency: "USD",
          price: values.price,
          availability: "http://schema.org/InStock",
        },
        review: values.reviews.map((review) => ({
          "@type": "Review",
          reviewBody: review.reviewBody,
          author: {
            "@type": "Person",
            name: review.reviewerName,
          },
          reviewRating: {
            "@type": "Rating",
            ratingValue: review.ratingValue,
          },
        })),
      };
      onGenerate(schema, "product");
    },
  });

  const basicFields = [
    { name: "name", label: "Product Name" },
    { name: "description", label: "Product Description", multiline: true, rows: 3 },
    { name: "price", label: "Product Price", type: "number" },
    { name: "image", label: "Product Image URL" },
  ];

  const brandFields = [
    { name: "brandName", label: "Brand Name" },
    { name: "brandImageLink", label: "Brand Image Link" },
    { name: "brandLink", label: "Brand Link" },
  ];

  const ratingFields = [
    { name: "ratingValue", label: "Rating Value", type: "number" },
    { name: "reviewCount", label: "Review Count", type: "number" },
  ];

  return (
    <Box sx={{ p: 3 }}>
      
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Product Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Product Information
            </Typography>
            {basicFields.map((field) => (
              <TextField
                key={field.name}
                fullWidth
                margin="normal"
                variant="outlined"
                type={field.type || "text"}
                name={field.name}
                label={field.label}
                multiline={field.multiline}
                rows={field.rows}
                value={formik.values[field.name]}
                onChange={formik.handleChange}
                error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                helperText={formik.touched[field.name] && formik.errors[field.name]}
              />
            ))}
          </Grid>
          
          {/* Brand and Rating Info */}
          <Grid item xs={12} md={6}>
            <Box mb={3}>
              <Typography variant="h6" gutterBottom>
                Brand Information
              </Typography>
              {brandFields.map((field) => (
                <TextField
                  key={field.name}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  type="text"
                  name={field.name}
                  label={field.label}
                  value={formik.values[field.name]}
                  onChange={formik.handleChange}
                  error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                  helperText={formik.touched[field.name] && formik.errors[field.name]}
                />
              ))}
            </Box>
            
            <Box>
              <Typography variant="h6" gutterBottom>
                Rating Information
              </Typography>
              {ratingFields.map((field) => (
                <TextField
                  key={field.name}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  type={field.type || "text"}
                  name={field.name}
                  label={field.label}
                  value={formik.values[field.name]}
                  onChange={formik.handleChange}
                  error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                  helperText={formik.touched[field.name] && formik.errors[field.name]}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
        
        {/* Reviews Section */}
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Product Reviews
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          {formik.values.reviews.map((review, index) => (
            <Box key={index} mb={4} p={2} sx={{ border: "1px solid #eee", borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                    variant="outlined"
                    name={`reviews[${index}].reviewBody`}
                    label="Review Body"
                    value={review.reviewBody}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.reviews?.[index]?.reviewBody &&
                      Boolean(formik.errors.reviews?.[index]?.reviewBody)
                    }
                    helperText={
                      formik.touched.reviews?.[index]?.reviewBody &&
                      formik.errors.reviews?.[index]?.reviewBody
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    name={`reviews[${index}].reviewerName`}
                    label="Reviewer Name"
                    value={review.reviewerName}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.reviews?.[index]?.reviewerName &&
                      Boolean(formik.errors.reviews?.[index]?.reviewerName)
                    }
                    helperText={
                      formik.touched.reviews?.[index]?.reviewerName &&
                      formik.errors.reviews?.[index]?.reviewerName
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    type="number"
                    name={`reviews[${index}].ratingValue`}
                    label="Rating Value"
                    value={review.ratingValue}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.reviews?.[index]?.ratingValue &&
                      Boolean(formik.errors.reviews?.[index]?.ratingValue)
                    }
                    helperText={
                      formik.touched.reviews?.[index]?.ratingValue &&
                      formik.errors.reviews?.[index]?.ratingValue
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
          
          <Box textAlign="center" mt={2}>
            <Button
              startIcon={<AddCircleOutlineIcon />}
              variant="outlined"
              onClick={() =>
                formik.setFieldValue("reviews", [
                  ...formik.values.reviews,
                  { reviewBody: "", reviewerName: "", ratingValue: "" },
                ])
              }
            >
              Add Another Review
            </Button>
          </Box>
        </Box>
        
        <Box mt={4} textAlign="center">
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={!formik.isValid || !formik.dirty}
          >
            Generate Product Schema
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ProductSchemaForm;